/**
 * Document → IR.
 *
 * Blocks describe slots as `IRSlot` holes. The React renderer fills them with Puck's slot
 * component; the exporters fill them by walking the child blocks stored at that prop path
 * and recursing into their own `toIR`. Two resolvers, one tree shape — which is the whole
 * point of the IR.
 *
 * Exporters must never see an unexpanded slot, so `emitXscp` throws on one.
 */

import type {
  BlockRegistry,
  CollctivDoc,
  IRContext,
  IRNode,
  PuckItem,
} from '@lp/shared';

/** Puck stores slot contents as an array of items under the slot's prop name. */
function slotItems(props: Record<string, unknown>, name: string): PuckItem[] {
  const value = props[name];
  return Array.isArray(value) ? (value as PuckItem[]) : [];
}

function contextFor(doc: CollctivDoc, isFirstSection: boolean): IRContext {
  return {
    docType: doc.docType,
    geo: doc.meta.geo,
    isFirstSection,
  };
}

/** Replace every `IRSlot` with the IR of the blocks stored at that prop path. */
function expand(
  node: IRNode,
  props: Record<string, unknown>,
  registry: BlockRegistry,
  ctx: IRContext,
): IRNode[] {
  switch (node.kind) {
    case 'slot': {
      const children = slotItems(props, node.name).flatMap((item) => {
        const spec = registry.get(item.type);
        // Children of a slot are never the first section, whatever the parent is.
        const childCtx: IRContext = { ...ctx, isFirstSection: false };
        const childIR = spec.toIR(item.props as never, childCtx);
        return expand(childIR, item.props, registry, childCtx);
      });

      // A slot that declares a container builds it here, mirroring the wrapper div Puck's
      // drop zone renders in the editor. Without this the exported markup would have one
      // fewer level than the preview, and a grid would lay out differently.
      if (node.wf && node.tag) {
        return [
          {
            kind: 'el',
            wf: node.wf,
            tag: node.tag,
            cls: node.cls ?? [],
            children,
            ...(node.displayName ? { displayName: node.displayName } : {}),
          },
        ];
      }
      return children;
    }

    case 'el': {
      const children = node.children.flatMap((child) => expand(child, props, registry, ctx));
      return [{ ...node, children }];
    }

    default:
      return [node];
  }
}

/**
 * Turn a document into top-level IR trees, one per section.
 *
 * Returned per-section rather than concatenated because the clipboard path copies one
 * section at a time — no authoritative payload size limit is documented, and pasting a whole
 * page at once is the reported way to hit trouble.
 */
export interface IRSection {
  /** The block that produced it, for the export panel's row label. */
  blockId: string;
  blockType: string;
  label: string;
  node: IRNode;
}

export function docToSections(doc: CollctivDoc, registry: BlockRegistry): IRSection[] {
  return doc.puck.content.map((item, index) => {
    const spec = registry.get(item.type);
    const ctx = contextFor(doc, index === 0);
    const [node] = expand(spec.toIR(item.props as never, ctx), item.props, registry, ctx);
    if (!node) {
      throw new Error(`Block "${item.type}" produced no IR node.`);
    }
    return {
      blockId: item.props.id,
      blockType: item.type,
      label: spec.label,
      node,
    };
  });
}

/** The whole document as one flat IR list — used by the HTML emitter and by tests. */
export function docToIR(doc: CollctivDoc, registry: BlockRegistry): IRNode[] {
  return docToSections(doc, registry).map((s) => s.node);
}
